<template>
  <div class="ce-sevtoggles">
    <span class="ce-sevtoggles__label">{{ label }}</span>
    <button
      v-for="s in SEVERITY_SERIES"
      :key="s.key"
      type="button"
      class="ce-sevtoggle"
      :class="{ 'is-off': !isOn(s.key), 'is-empty': isEmpty(s.key) }"
      :aria-pressed="isOn(s.key) ? 'true' : 'false'"
      :title="hintFor(s)"
      @click="toggle(s.key)"
    >
      <span class="ce-sevtoggle__dot" :style="{ background: SEVERITY_COLORS[s.key] }" />
      {{ s.label }}
      <span v-if="hasCounts" class="ce-sevtoggle__n">{{ countText(s.key) }}</span>
    </button>
  </div>
</template>

<script>
  import { SEVERITY_SERIES, SEVERITY_COLORS, SEVERITY_STACK_ORDER } from '../consts/ChangeEvents.consts';

  /**
   * The severity switches that sit above every chart in Change Events.
   *
   * Four switches, HIGH MEDIUM LOW and Not scored, each on or off on its own,
   * and any number on at once. All four start on. A severity with no data in
   * the period still gets a switch, drawn faint, so a person can see that HIGH
   * exists and is zero rather than wondering where it went.
   *
   * This component only reports the choice. The chart hides datasets on the
   * client, so switching is instant and nothing is fetched again.
   */
  export default {
    name: 'SeverityToggle',
    model: {
      prop: 'value',
      event: 'change'
    },
    props: {
      /** The severities that are on, as `HIGH` `MEDIUM` `LOW` `unscored`. */
      value: {
        type: Array,
        default: () => SEVERITY_STACK_ORDER.slice()
      },
      /** Wording in front of the switches. */
      label: {
        type: String,
        default: 'Show severity:'
      },
      /**
       * How many rows each severity has in this period, keyed by severity.
       * Optional. When given, the count is shown and an empty severity is drawn
       * faint instead of being hidden.
       */
      counts: {
        type: Object,
        default: null
      }
    },
    data() {
      return { SEVERITY_SERIES, SEVERITY_COLORS };
    },
    computed: {
      hasCounts() {
        return !!this.counts;
      }
    },
    methods: {
      isOn(key) {
        return this.value.indexOf(key) !== -1;
      },
      /** True when this severity has nothing in the period on screen. */
      isEmpty(key) {
        if (!this.counts) return false;
        return !Number(this.counts[key]);
      },
      countText(key) {
        const num = Number(this.counts && this.counts[key]);
        return Number.isFinite(num) ? num.toLocaleString('en-US') : '0';
      },
      hintFor(series) {
        if (this.isEmpty(series.key)) return `No ${series.label} in this period`;
        return this.isOn(series.key) ? `Hide ${series.label}` : `Show ${series.label}`;
      },
      /**
       * Turns one severity on or off and reports the whole new choice, always
       * in the worst-first order the charts stack in.
       * @param {string} key
       */
      toggle(key) {
        const next = this.isOn(key) ? this.value.filter((k) => k !== key) : this.value.concat([key]);
        this.$emit(
          'change',
          SEVERITY_STACK_ORDER.filter((k) => next.indexOf(k) !== -1)
        );
      }
    }
  };
</script>

<style lang="scss" scoped>
  .ce-sevtoggles {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;

    &__label {
      font-size: 12px;
      color: #8a94a0;
    }
  }
  .ce-sevtoggle {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: #ffffff;
    border: 1px solid #e3e7ea;
    border-radius: 999px;
    padding: 2px 10px;
    font-size: 12px;
    font-weight: 600;
    color: #232a31;
    cursor: pointer;
    line-height: 20px;

    &:hover {
      border-color: #20a4b8;
    }
    &:focus {
      outline: 2px solid rgba(32, 164, 184, 0.4);
      outline-offset: 1px;
    }
    &__dot {
      width: 9px;
      height: 9px;
      border-radius: 2px;
      display: inline-block;
    }
    &__n {
      color: #8a94a0;
      font-weight: 500;
    }
    // Switched off by the person: still readable, clearly not counted.
    &.is-off {
      color: #8a94a0;
      background: #f4f6f8;

      .ce-sevtoggle__dot {
        opacity: 0.3;
      }
    }
    // Nothing of this severity in the period. Shown, never hidden.
    &.is-empty {
      border-style: dashed;
      color: #8a94a0;
    }
  }
</style>
